import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { satPeriodRange } from "@/lib/satPeriodRange";
import ExportToolbar from "@/components/ExportToolbar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarRange } from "lucide-react";

const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

/**
 * Exportación del periodo (emitidos + recibidos) desde el hub Similar SAT.
 * Solo lee XML ya en MongoDB; no interfiere con descarga automática SAT.
 */
export default function VisoresSatHubExportPanel() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [year, setYear] = useState(() => {
    const saved = Number(localStorage.getItem("vf:selected_year") || 0);
    if (saved >= 2020 && saved <= 2099) return saved;
    const now = new Date();
    return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  });
  const [satMes, setSatMes] = useState("all");

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => setClients([]));
  }, []);

  useEffect(() => {
    if (year >= 2020 && year <= 2099) {
      try {
        localStorage.setItem("vf:selected_year", String(year));
      } catch {
        /* ignore */
      }
    }
  }, [year]);

  const clientRfc = clients.find((c) => c.id === clientId)?.rfc || "";
  const clientSelectValue = clients.length && clients.some((c) => c.id === clientId) ? clientId : undefined;
  const years = [year + 1, year, year - 1, year - 2, year - 3, year - 4];
  const period = useMemo(() => satPeriodRange(year, satMes), [year, satMes]);

  return (
    <div
      className="rounded-sm border border-slate-200 bg-white px-4 py-3 flex flex-wrap items-center gap-2"
      data-testid="visores-hub-export-panel"
    >
      <span className="text-xs font-semibold text-slate-800 mr-1">Exportar periodo (MongoDB)</span>
      <Select value={clientSelectValue} onValueChange={setClientId}>
        <SelectTrigger className="w-52 h-8 rounded-sm text-xs" data-testid="hub-export-client-select">
          <SelectValue placeholder="Cliente" />
        </SelectTrigger>
        <SelectContent>
          {clients.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              {c.razon_social} — {c.rfc}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
        <SelectTrigger className="w-24 h-8 rounded-sm text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {years.map((y) => (
            <SelectItem key={y} value={String(y)}>
              {y}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex items-center gap-1 border border-slate-200 rounded-sm pl-2 pr-1 h-8 bg-white">
        <CalendarRange className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <Select value={satMes} onValueChange={setSatMes}>
          <SelectTrigger className="h-7 min-w-[10rem] text-xs border-0 shadow-none">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todo el año</SelectItem>
            {MONTH_NAMES.map((nombre, i) => (
              <SelectItem key={nombre} value={String(i + 1).padStart(2, "0")}>
                {nombre} {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <ExportToolbar
        clientId={clientId}
        clientRfc={clientRfc}
        fechaInicio={period.from}
        fechaFin={period.to}
        direccion="emitido"
        showAmbosFlujos
        exportAlignVisor
        hubMode
        disabled={!clientId}
        compact
      />
      <span className="text-[10px] text-slate-500 w-full sm:w-auto">
        Solo comprobantes ya cargados en el sistema para ese periodo (emitidos y recibidos).
      </span>
    </div>
  );
}
